import Swiper from 'swiper';
import { A11y, Autoplay, Navigation } from 'swiper/modules';

let userUsedKeyboard = false;

window.addEventListener('keydown', e => {
  if (e.key === 'Tab') {
    userUsedKeyboard = true;
  }
});

document.addEventListener('focusin', e => {
  if (!userUsedKeyboard) return;

  const carousel = e.target.closest('.swiper-carousel');
  if (!carousel) return;

  const swiperInstance = carousel.swiper;
  if (!swiperInstance) return;

  if (swiperInstance.params.loop) {
    if (swiperInstance.autoplay?.running) {
      swiperInstance.autoplay.stop();
    }

    swiperInstance.destroy(true, true);

    setTimeout(() => {
      initCarousels([carousel]);
      updateButtonUI(carousel, false);
    }, 5);
  }
});

document.addEventListener('DOMContentLoaded', () => {
  const carouselList = document.querySelectorAll('.swiper-carousel');
  initCarousels(carouselList);
});

export function initCarousels(carouselList) {
  carouselList.forEach(carousel => {
    let isAutoPlay = carousel.classList.contains('autoplay');
    let isLoop = carousel.classList.contains('loop') && !userUsedKeyboard;
    let countSlides = carousel.querySelectorAll('.swiper-slide').length;

    new Swiper(carousel, {
      modules: [Navigation, A11y, Autoplay],
      slidesPerView: 1,
      spaceBetween: 40,
      loop: isLoop,
      autoplay: isAutoPlay,
      navigation: {
        nextEl: '.btn-carousel-next',
        prevEl: '.btn-carousel-prev',
      },
      breakpoints: {
        700: {
          slidesPerView: Math.min(countSlides, 2),
        },
        992: {
          slidesPerView: Math.min(countSlides, 3),
        },
        1440: {
          slidesPerView: Math.min(countSlides, 4),
        },
      },
      on: {
        init() {
          updateSlideAccessibility(this);
          updateNavigationAccessibility(this);
          setSwiperButtonAriaLabels(this.el);

          if (this.params.autoplay && this.autoplay?.running) {
            setupAutoplayFocusHandling(this);
          }
        },
        slideChange() {
          updateSlideAccessibility(this);
          updateNavigationAccessibility(this);
        },
        resize() {
          updateNavigationAccessibility(this);
        },
      },
    });

    const stopButton = carousel.nextElementSibling?.querySelector('.btn-stop-autoplay');
    const swiper = carousel.swiper;

    if (stopButton && swiper?.autoplay) {
      updateButtonUI(carousel, swiper.autoplay.running);

      stopButton.addEventListener('click', () => {
        if (swiper.autoplay.running) {
          swiper.autoplay.stop();
        } else {
          swiper.autoplay.start();
          setupAutoplayFocusHandling(swiper);
        }
        updateButtonUI(carousel, swiper.autoplay.running);
      });
    }

    carousel.addEventListener('keydown', e => {
      if (e.key === 'Escape') {
        let next = carousel;
        while ((next = next.nextElementSibling)) {
          const focusable = next.querySelector('a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])');
          if (focusable) {
            focusable.focus();
            break;
          }
        }
      }
    });
  });

  document.querySelectorAll('.menu-link').forEach(button => {
    button.addEventListener(
      'click',
      () => {
        if (button.classList.contains('current')) {
          return;
        }

        const targetId = button.dataset.id;
        const targetDiv = document.getElementById(targetId);

        if (targetDiv && targetDiv.querySelector('.swiper-carousel')) {
          const targetCarousels = targetDiv.querySelectorAll('.swiper-carousel');

          targetCarousels.forEach(carousel => {
            if (carousel.swiper) {
              carousel.swiper.destroy(true, true);
            }
          });

          setTimeout(() => {
            if (!targetCarousels[0].swiper) {
              initCarousels(targetCarousels);
            }
          }, 1);
        }
      },
      { capture: true },
    );
  });
}

export function updateNavigationAccessibility(swiper) {
  const nextBtn = swiper.navigation?.nextEl;
  const prevBtn = swiper.navigation?.prevEl;

  [nextBtn, prevBtn].forEach(btn => {
    if (!btn) return;
    if (btn.classList.contains('swiper-button-lock')) {
      btn.setAttribute('aria-hidden', 'true');
    } else {
      btn.setAttribute('aria-hidden', 'false');
    }
  });
}

export function setSwiperButtonAriaLabels(swiperEl) {
  requestAnimationFrame(() => {
    const prevBtn = swiperEl.querySelector('.swiper-button-prev');
    const nextBtn = swiperEl.querySelector('.swiper-button-next');

    if (prevBtn?.dataset.labelPrev) {
      prevBtn.setAttribute('aria-label', prevBtn.dataset.labelPrev);
    }
    if (nextBtn?.dataset.labelNext) {
      nextBtn.setAttribute('aria-label', nextBtn.dataset.labelNext);
    }
  });
}

export function updateSlideAccessibility(swiper) {
  const visibleIndexes = [];

  for (let i = swiper.activeIndex; i < swiper.activeIndex + swiper.params.slidesPerView; i++) {
    visibleIndexes.push(i % swiper.slides.length);
  }

  swiper.slides.forEach((slide, index) => {
    if (visibleIndexes.includes(index)) {
      slide.removeAttribute('aria-hidden');
    } else {
      slide.setAttribute('aria-hidden', 'true');
    }
  });
}

function setupAutoplayFocusHandling(swiper) {
  const onFocusStopAutoplay = () => {
    const activeEl = document.activeElement;
    if (!swiper?.el?.contains(activeEl)) return;

    swiper.autoplay?.stop();
    updateButtonUI(swiper.el, false);
    document.removeEventListener('keydown', onFocusStopAutoplay);
  };

  document.addEventListener('keydown', onFocusStopAutoplay);
}

function updateButtonUI(carouselOrSwiperEl, isRunning) {
  const carousel = carouselOrSwiperEl.el || carouselOrSwiperEl;
  const stopButton = carousel.nextElementSibling?.querySelector('.btn-stop-autoplay');
  if (!stopButton) return;

  const icon = stopButton.querySelector('img');
  const text = stopButton.querySelector('span');

  const pauseText = text?.dataset.textPause;
  const resumeText = text?.dataset.textResume;

  const pauseAria = stopButton.dataset.textButtonPause;
  const resumeAria = stopButton.dataset.textButtonResume;

  if (isRunning) {
    if (text) text.textContent = pauseText;
    if (icon) {
      icon.src = '/themes/vasco-theme/img/pause.svg';
    }
    stopButton.setAttribute('aria-label', pauseAria);
  } else {
    if (text) text.textContent = resumeText;
    if (icon) {
      icon.src = '/themes/vasco-theme/img/play-circle.svg';
    }
    stopButton.setAttribute('aria-label', resumeAria);
  }
}
